import { Button, Drawer, Form, FormInstance, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  Cards as CardsIcon,
  List as ListIcon,
  Filter as FilterIcon,
  XmarkSolid,
} from '../../utils/icons';
import { useCallback, useEffect, useState } from 'react';
import PaginationMemorial from '../../DLS/Pagination';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useIsMobile } from '../../utils/hooks';
import { getAllCountries, getFormattedDateToShow } from '../../utils';
import AvatarMemorial from '../../DLS/Avatar';
import useApi from '../../utils/api';
import Loader from '../../DLS/Loader';
import MemorialCards from '../../components/MemorialCards';
import { MemorialInCard } from '../../components/MemorialCards/types';

const countries = getAllCountries();

type View = 'cards' | 'list';

const Filter = ({
  form,
  cleanFilters,
  onFilter,
}: {
  form: FormInstance;
  cleanFilters: () => void;
  onFilter: () => void;
}) => {
  let [searchParams] = useSearchParams();

  useEffect(() => {
    form.setFieldsValue({
      firstName: searchParams.get('firstName'),
      lastName: searchParams.get('lastName'),
      city: searchParams.get('city'),
      country: searchParams.get('country'),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const { t } = useTranslation();

  return (
    <div className="flex flex-col md:flex-row md:items-center">
      <Form form={form} className="grid flex-1 md:grid-cols-4 md:gap-[16px]" onFinish={onFilter}>
        <Form.Item
          name="firstName"
          label={t('common.firstName')}
          className="mb-[16px] md:mb-[24px]"
        >
          <Input placeholder={String(t('common.firstName'))} />
        </Form.Item>
        <Form.Item name="lastName" label={t('common.lastName')} className="mb-[16px] md:mb-[24px]">
          <Input placeholder={String(t('common.lastName'))} />
        </Form.Item>
        <Form.Item name="country" label={t('common.country')} className="mb-[16px] md:mb-[24px]">
          <Select
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              !!String(option?.value).toLocaleLowerCase().includes(input.toLocaleLowerCase())
            }
            placeholder={String(t('common.country'))}
            notFoundContent={<p className="px-[16px] py-[8px]">{t('common.withoutResults')}</p>}
          >
            {countries.map((country) => (
              <Select.Option value={country.name} key={`country-code-${country.isoCode}`}>
                <span className="mr-[8px]">{country.flag}</span>
                {country.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="city" label={t('common.city')} className="mb-[16px] md:mb-[24px]">
          <Input placeholder={String(t('common.city'))} maxLength={50} />
        </Form.Item>
      </Form>
      <div className="footerButtons flex max-w-[calc(100%-32px)] flex-row justify-end md:ml-[16px] md:max-w-none">
        <Button
          className="md:mr-[8px]"
          onClick={() => {
            form.resetFields();
            cleanFilters();
          }}
        >
          {t('start.cleanFilters')}
        </Button>
        <Button
          htmlType="submit"
          type="primary"
          className="mb-[8px] min-w-fit md:mb-[0px]"
          onClick={() => form.submit()}
        >
          {t('common.filter')}
        </Button>
      </div>
    </div>
  );
};

const List = ({ memorials }: { memorials: MemorialInCard[] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = window.innerWidth < 768;

  return (
    <>
      {memorials.map(
        ({ dateOfDeath, dieCity, dieCountry, firstName, id, image, lastName }, index) => (
          <div
            key={`card-${index}`}
            className={`mb-[16px] flex items-center justify-between rounded-[8px] border border-solid border-bg1 bg-white p-[16px] md:p-[24px]`}
            onClick={
              isMobile ? () => navigate(`/home/memorial/${id}?from=explore-memorials`) : undefined
            }
          >
            <div className="flex items-center">
              <div className="mr-[8px] md:mr-[24px]">
                <AvatarMemorial
                  image={image}
                  initials={`${firstName[0]}${lastName[0]}`}
                  size={48}
                  shape="square"
                />
              </div>
              <div>
                <h3 className="m-[0px] hidden md:block">{`${firstName} ${lastName}`}</h3>
                <h5 className="m-[0px] block md:hidden">{`${firstName} ${lastName}`}</h5>
                <p className="mt-[4px] hidden md:block">
                  {t('memorial.dieIn', {
                    city: dieCity || dieCountry,
                    dateOfDeath: getFormattedDateToShow(dateOfDeath),
                  })}
                </p>
                <p className="smallParagraph mt-[4px] block md:hidden">
                  {t('memorial.dieIn', {
                    city: dieCity || dieCountry,
                    dateOfDeath: getFormattedDateToShow(dateOfDeath),
                  })}
                </p>
              </div>
            </div>
            <p
              onClick={() => navigate(`/home/memorial/${id}?from=explore-memorials`)}
              className="link hidden md:block"
            >
              {t('start.gotToMemorial')}
            </p>
          </div>
        )
      )}
    </>
  );
};

const Start = () => {
  const isMobile = useIsMobile();
  let [searchParams] = useSearchParams();
  const [view, setView] = useState<View>(isMobile ? 'list' : 'cards');
  const [memorials, setMemorials] = useState<MemorialInCard[]>([]);
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalMemorials, setTotalMemorials] = useState<number>(0);
  const [hasActiveFilter, setHasActiveFilter] = useState<boolean>(
    searchParams.has('firstName') ||
      searchParams.has('lastName') ||
      searchParams.has('city') ||
      searchParams.has('country')
  );
  const [form] = Form.useForm();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api } = useApi();

  const updateURL = ({
    applyFilter,
    cleanFilters,
    page,
    pageSize,
  }: {
    applyFilter?: boolean;
    cleanFilters?: boolean;
    page: number;
    pageSize?: number;
  }) => {
    const { firstName, lastName, city, country } = form.getFieldsValue();
    let url = `/home/start?page=${page}`;
    url += `&pageSize=${pageSize || searchParams.get('pageSize')}`;

    if (cleanFilters) {
      setHasActiveFilter(false);
    } else if (applyFilter) {
      if (firstName) url += `&firstName=${firstName}`;
      if (lastName) url += `&lastName=${lastName}`;
      if (city) url += `&city=${city}`;
      if (country) url += `&country=${country}`;
      setHasActiveFilter(true);
    } else if (hasActiveFilter) {
      if (searchParams.has('firstName')) url += `&firstName=${searchParams.get('firstName')}`;
      if (searchParams.has('lastName')) url += `&lastName=${searchParams.get('lastName')}`;
      if (searchParams.has('city')) url += `&city=${searchParams.get('city')}`;
      if (searchParams.has('country')) url += `&country=${searchParams.get('country')}`;
    }

    navigate(url, { replace: true });
  };

  const getMemorials = useCallback(() => {
    const params: any = { rows: searchParams.get('pageSize'), init_page: searchParams.get('page') };
    if (searchParams.has('firstName')) params['name'] = searchParams.get('firstName');
    if (searchParams.has('lastName')) params['last_name'] = searchParams.get('lastName');
    if (searchParams.has('city')) params['die_city'] = searchParams.get('city');
    if (searchParams.has('country')) params['country'] = searchParams.get('country');

    setIsLoading(true);
    api()
      .get(`/personal-info-all`, { params })
      .then((response) => {
        if (response.status === 200) {
          const { items, rows } = response.data.data;
          setTotalMemorials(rows);
          setMemorials(
            items.map((e: any) => ({
              dateOfDeath: e.die_date,
              dieCity: e.die_city,
              dieCountry: e.country,
              firstName: e.name,
              lastName: e.lastname,
              image: e.photo,
              id: e.id,
            }))
          );
        }
      })
      .finally(() => setIsLoading(false));
  }, [api, searchParams]);

  useEffect(() => {
    getMemorials();
  }, [getMemorials]);

  return (
    <div className={`z-10 box-border flex min-h-[calc(100vh-80px)] flex-col px-[16px]`}>
      <Drawer
        title={t('start.filterMemorial')}
        placement="bottom"
        onClose={() => setShowFilterModal(false)}
        open={showFilterModal}
        closeIcon={<XmarkSolid />}
        rootClassName="drawerMobile"
        size="large"
      >
        <Filter
          form={form}
          onFilter={() => {
            setShowFilterModal(false);
            updateURL({ page: 1, applyFilter: true });
          }}
          cleanFilters={() => {
            setShowFilterModal(false);
            updateURL({ page: 1, cleanFilters: true });
          }}
        />
      </Drawer>

      <div className="mx-auto mt-[24px] w-full max-w-[1024px] md:mt-[40px]">
        <h1 className="mb-[32px] mt-[0px] hidden md:block">{t('start.exploreMemorials')}</h1>
        <div className="hidden md:block">
          {!isMobile && (
            <Filter
              form={form}
              onFilter={() => updateURL({ page: 1, applyFilter: true })}
              cleanFilters={() => updateURL({ page: 1, cleanFilters: true })}
            />
          )}
          <div className="mb-[24px] mt-[40px] flex items-center justify-between">
            <h3 className="m-[0px]">{t('start.memorials')}</h3>
            <div className="flex rounded-[8px] bg-bg1 p-[4px]">
              <div
                className={`flex cursor-pointer rounded-[8px] p-[8px] ${
                  view === 'cards'
                    ? 'cursor-default bg-bg4'
                    : '[&>svg>path]:stroke-text3 [&>svg>rect]:stroke-text3'
                }`}
                onClick={view === 'cards' ? undefined : () => setView('cards')}
              >
                <CardsIcon />
              </div>
              <div
                className={`flex cursor-pointer rounded-[8px] p-[8px] ${
                  view === 'list'
                    ? 'cursor-default bg-bg4'
                    : '[&>svg>circle]:fill-text3 [&>svg>path]:stroke-text3'
                }`}
                onClick={view === 'list' ? undefined : () => setView('list')}
              >
                <ListIcon />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-[16px] flex items-center justify-between md:hidden">
          <h4 className="my-0">{t('start.exploreMemorials')}</h4>
          <Button className="mr-[8px] h-[32px]" onClick={() => setShowFilterModal(true)}>
            <div className="flex items-center">
              <p className="smallParagraph">{t('common.filter')}</p>
              <FilterIcon />
            </div>
          </Button>
        </div>
        {isLoading && (
          <div className="flex min-h-[400px] items-center justify-center bg-gradient1">
            <Loader />
          </div>
        )}
        {!isLoading && view === 'list' && <List memorials={memorials} />}
        {!isLoading && view === 'cards' && <MemorialCards memorials={memorials} />}
        {memorials.length !== 0 && (
          <div className="mt-[24px]">
            <PaginationMemorial
              defaultPageSize={
                searchParams.has('pageSize') ? Number(searchParams.get('pageSize')) : 3
              }
              defaultPage={searchParams.has('page') ? Number(searchParams.get('page')) : 1}
              onChange={({ page, pageSize }) => updateURL({ page, pageSize })}
              totalElements={totalMemorials}
              pageSizeOptions={[3, 6, 9, 12]}
            />
          </div>
        )}
        {!isLoading && memorials.length === 0 && (
          <div className="flex min-h-[400px] flex-col items-center justify-center">
            <h3 className="mb-[24px] max-w-[400px] text-center">
              {t('start.withoutResults.title')}
            </h3>
            <p className="max-w-[400px] text-center">{t('start.withoutResults.text')}</p>
          </div>
        )}
      </div>
      <div className="mb-[24px] md:mb-[40px]"></div>
    </div>
  );
};

export default Start;
